type GraphemeSegmenter = {
  segment(input: string): Iterable<{ segment: string }>;
};

type IntlWithSegmenter = typeof Intl & {
  Segmenter?: new (
    locale?: string,
    options?: { granularity: "grapheme" },
  ) => GraphemeSegmenter;
};

// Split on grapheme clusters where the browser supports it so the tail never
// starts mid-emoji/mid-surrogate; code-unit slice is the fallback.
function toGraphemes(text: string): string[] {
  const SegmenterCtor = (Intl as IntlWithSegmenter).Segmenter;
  if (SegmenterCtor) {
    const segments = new SegmenterCtor(undefined, {
      granularity: "grapheme",
    }).segment(text);
    return Array.from(segments, (s) => s.segment);
  }
  return text.split("");
}

/** Split text into [head, tail]; head is empty when the text is tail-sized. */
export function splitTail(text: string, tailChars: number): [string, string] {
  tailChars = Math.max(0, Math.floor(tailChars));
  const graphemes = toGraphemes(text);
  if (graphemes.length <= tailChars) {
    return ["", text];
  }
  return [
    graphemes.slice(0, -tailChars).join(""),
    graphemes.slice(-tailChars).join(""),
  ];
}

/**
 * Middle-splitting only pays off when the head is long enough to absorb the
 * shrinking. Below this head length, a split would just hide the first few
 * characters in narrow containers (a 15-char name became "iny-man" with no
 * ellipsis) — plain end-truncation reads better there.
 */
const MIN_HEAD_CHARS = 6;

/**
 * Single-line truncation that ellipsizes the MIDDLE of the string instead of
 * the end, keeping the tail visible — for names that differ only in their
 * final characters (long vendor filenames). Pure CSS: the head shrinks and
 * ellipsizes first (999× shrink factor); in containers too narrow for even
 * the tail, the tail end-truncates as a last resort instead of clipping the
 * start of the name. The full text is always available via tooltip and to
 * screen readers. Short names skip the split and end-truncate normally.
 */
export function MiddleTruncate({
  text,
  tailChars = 14,
  className = "",
}: {
  text: string;
  tailChars?: number;
  className?: string;
}) {
  const [head, tail] = splitTail(text, tailChars);

  if (head.length < MIN_HEAD_CHARS) {
    return (
      <span dir="auto" title={text} className={`block truncate ${className}`}>
        {text}
      </span>
    );
  }

  return (
    <span
      dir="auto"
      title={text}
      aria-label={text}
      className={`flex min-w-0 overflow-hidden ${className}`}
    >
      <span
        aria-hidden="true"
        className="min-w-0 truncate"
        style={{ flexShrink: 999 }}
      >
        {head}
      </span>
      <span aria-hidden="true" className="min-w-0 truncate whitespace-pre">
        {tail}
      </span>
    </span>
  );
}
